import { BaseModel } from '@cotera/client/v0/actions/framework/model.base';
import {
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework/watchable';
import {
  BroadcastEventType,
  type BroadcastEventPayloadMap,
  type ChatStatusFrameOutput,
} from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import { getStackFrameStatusInfo } from '../utils/coco-stack-frame-status';
import { useStatefullyAwareInScopeModel } from '@cotera/client/v0/actions/framework';
import { WatchablesFactory } from '../../watchables/watchables-factory';

type Props = {
  conversationId: string;
  client: TenantedClient['resource']['chat'];
  watchablesFactory: Pick<WatchablesFactory, 'makeStackFrameWrittenWatchable'>;
};

type StackFrameWrittenPayload =
  BroadcastEventPayloadMap[typeof BroadcastEventType.StackFrameWritten];

export const RECENT_CHAT_SHIMMER_DEBOUNCE_MS = 2000;

export function isRecentChatInNonTerminalState(
  stackFrames: ChatStatusFrameOutput[]
): boolean {
  const statusInfo = getStackFrameStatusInfo(stackFrames);
  return statusInfo.isTerminal === false;
}

export function linkDebouncedFalseTransition({
  sourceWatchable,
  targetWatchable,
  delayMs,
}: {
  sourceWatchable: ReadonlyWatchable<boolean>;
  targetWatchable: Watchable<boolean>;
  delayMs: number;
}): () => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const clearDebounceTimeout = (): void => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const setDebouncedValue = (nextValue: boolean): void => {
    if (nextValue) {
      clearDebounceTimeout();
      targetWatchable.set(true);
      return;
    }

    // Only debounce a transition from true -> false. If the target is
    // already false, there is nothing to do and we avoid scheduling
    // an unnecessary timeout + redundant set(false).
    clearDebounceTimeout();
    if (targetWatchable.snapshot() === false) {
      return;
    }

    timeoutId = setTimeout(() => {
      timeoutId = null;
      if (sourceWatchable.snapshot() === false) {
        targetWatchable.set(false);
      }
    }, delayMs);
  };

  setDebouncedValue(sourceWatchable.snapshot());
  const unsubscribe = sourceWatchable.subscribe(setDebouncedValue);

  return () => {
    clearDebounceTimeout();
    unsubscribe();
  };
}

export class RecentChatModel extends BaseModel {
  readonly conversationId: string;
  readonly stackFramesWatchable: ReadonlyEventWatchable<
    ChatStatusFrameOutput[]
  >;
  readonly isReplyingWatchable: ReadonlyWatchable<boolean>;
  private readonly _unsubscribeReplyingDebounce: () => void;

  constructor({ conversationId, client, watchablesFactory }: Props) {
    super(conversationId);
    this.conversationId = conversationId;

    const eventWatchablesFactory = watchablesFactory;

    this.stackFramesWatchable =
      eventWatchablesFactory.makeStackFrameWrittenWatchable<
        ChatStatusFrameOutput[],
        StackFrameWrittenPayload
      >({
        runOnMount: true,
        initialValue: [],
        fn: async (payload, _get) => {
          if (payload !== undefined && payload !== null) {
            if (
              payload.conversationId === conversationId &&
              payload.stackFrames !== undefined
            ) {
              return payload.stackFrames;
            }
            if (
              payload.parentConversationId === conversationId &&
              payload.parentStackFrames !== undefined
            ) {
              return payload.parentStackFrames;
            }
          }

          const res = Assert.assertOk(
            await client.chatStatus({ conversationId })
          );
          return res.stackFrames;
        },
        filter: (payload) =>
          payload?.conversationAncestry?.includes(conversationId) ?? false,
        fallback: {
          fn: async () => {
            const res = Assert.assertOk(
              await client.chatStatus({ conversationId })
            );
            return res.stackFrames;
          },
          interval: 10_000,
        },
      });

    const isReplyingRawWatchable = Watchable.from((get) => {
      const stackFrames = get(this.stackFramesWatchable);
      return isRecentChatInNonTerminalState(stackFrames);
    });
    const isReplyingDebouncedWatchable = Watchable.fromValue(
      isReplyingRawWatchable.snapshot()
    );

    this._unsubscribeReplyingDebounce = linkDebouncedFalseTransition({
      sourceWatchable: isReplyingRawWatchable,
      targetWatchable: isReplyingDebouncedWatchable,
      delayMs: RECENT_CHAT_SHIMMER_DEBOUNCE_MS,
    });
    this.isReplyingWatchable = isReplyingDebouncedWatchable;
  }

  dispose(): void {
    this._unsubscribeReplyingDebounce();
    this.stackFramesWatchable.unsubscribe();
  }
}

export const useRecentChatModel = (
  conversationId: string
): RecentChatModel | null => {
  return useStatefullyAwareInScopeModel(RecentChatModel, conversationId);
};
